import { Button } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getLocale } from "../../../i18n";
import { useTranslation } from "../../../i18n/useTranslation";
import { DeviceService } from "../../../services/device.service";
import { HrReportService } from "../../../services/hr-report.service";
import { APP_ROUTES } from "../../constants/module";
import Loading from "../../components/Loading";
import "./index.scss";

type AttendanceTotals = {
  onTime: number;
  late: number;
  absent: number;
  missingCheckout: number;
  needAction: number;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour12: false,
  });
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<any[]>([]);
  const [offlineCount, setOfflineCount] = useState(0);
  const [totals, setTotals] = useState<AttendanceTotals | null>(null);
  const [reportDate, setReportDate] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [deviceRes, summaryRes] = await Promise.all([
        DeviceService.getDevices(),
        HrReportService.getSummary(),
      ]);
      const deviceData = deviceRes.data?.data;
      setDevices(deviceData?.devices || []);
      setOfflineCount(deviceData?.offlineCount || 0);

      const summary = summaryRes.data?.data;
      setTotals(summary?.totals || null);
      setReportDate(summary?.date || "");
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onlineCount = devices.filter((item) => item.status === "online").length;
  const missingCredentialCount = devices.filter((item) => !item.hasDeviceAccount).length;
  const missingRecoveryCount = devices.filter((item) => item.hasDeviceAccount && !item.hasRecoveryCode).length;
  const outdatedAppCount = devices.filter((item) => Number(item.appVersionCode || 0) < 17).length;

  const alerts = useMemo(() => {
    const items: Array<{ key: string; tone: "warn" | "info"; message: string; link?: string }> = [];
    if (offlineCount > 0) {
      items.push({
        key: "offline",
        tone: "warn",
        message: t("dashboard.alertOffline", { count: offlineCount }),
        link: APP_ROUTES.DEVICES,
      });
    }
    if (missingCredentialCount > 0) {
      items.push({
        key: "credential",
        tone: "warn",
        message: t("dashboard.alertNoCredential", { count: missingCredentialCount }),
        link: APP_ROUTES.DEVICES,
      });
    }
    if (missingRecoveryCount > 0) {
      items.push({
        key: "recovery",
        tone: "info",
        message: t("dashboard.alertNoRecovery", { count: missingRecoveryCount }),
        link: APP_ROUTES.DEVICES,
      });
    }
    if (outdatedAppCount > 0) {
      items.push({
        key: "app",
        tone: "info",
        message: t("dashboard.alertOldApp", { count: outdatedAppCount }),
        link: APP_ROUTES.DEVICES,
      });
    }
    if ((totals?.needAction || 0) > 0) {
      items.push({
        key: "attendance",
        tone: "warn",
        message: t("dashboard.alertNeedAction", { count: totals?.needAction || 0 }),
        link: APP_ROUTES.TIMESHEET_TIMEKEEPING,
      });
    }
    return items;
  }, [
    offlineCount,
    missingCredentialCount,
    missingRecoveryCount,
    outdatedAppCount,
    totals,
    t,
  ]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div>
          <h2>{t("dashboard.title")}</h2>
          {reportDate ? (
            <div className="dashboard__subtitle">
              {t("dashboard.reportDate", { date: reportDate })}
            </div>
          ) : null}
        </div>
        <Button onClick={fetchData}>{t("common.refresh")}</Button>
      </div>

      <div className="dashboard__cards">
        <div className="dashboard__card">
          <div className="dashboard__card-label">{t("dashboard.devicesOnline")}</div>
          <div className="dashboard__card-value dashboard__card-value--ok">
            {onlineCount}/{devices.length}
          </div>
        </div>
        <div className="dashboard__card">
          <div className="dashboard__card-label">{t("dashboard.onTime")}</div>
          <div className="dashboard__card-value">{totals?.onTime ?? 0}</div>
        </div>
        <div className="dashboard__card">
          <div className="dashboard__card-label">{t("dashboard.late")}</div>
          <div className="dashboard__card-value dashboard__card-value--warn">
            {totals?.late ?? 0}
          </div>
        </div>
        <div className="dashboard__card">
          <div className="dashboard__card-label">{t("dashboard.absent")}</div>
          <div className="dashboard__card-value dashboard__card-value--danger">
            {totals?.absent ?? 0}
          </div>
        </div>
        <div className="dashboard__card">
          <div className="dashboard__card-label">{t("dashboard.missingCheckout")}</div>
          <div className="dashboard__card-value">{totals?.missingCheckout ?? 0}</div>
        </div>
      </div>

      {alerts.length > 0 ? (
        <div className="dashboard__alerts">
          <h3>{t("dashboard.alertsTitle")}</h3>
          {alerts.map((alert) => (
            <div
              key={alert.key}
              className={`dashboard__alert dashboard__alert--${alert.tone}`}
            >
              <span>{alert.message}</span>
              {alert.link ? (
                <Link to={alert.link}>{t("dashboard.viewDetails")}</Link>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="dashboard__alerts dashboard__alerts--ok">
          {t("dashboard.allGood")}
        </div>
      )}

      <div className="dashboard__section">
        <div className="dashboard__section-header">
          <h3>{t("dashboard.recentDevices")}</h3>
          <Link to={APP_ROUTES.DEVICES}>{t("dashboard.viewAllDevices")}</Link>
        </div>
        <div className="dashboard__table">
          <div className="dashboard__row dashboard__row--head">
            <div>{t("devices.name")}</div>
            <div>{t("devices.status")}</div>
            <div>{t("devices.account")}</div>
            <div>{t("devices.lastSeen")}</div>
          </div>
          {devices.slice(0, 6).map((device) => (
            <div className="dashboard__row" key={device.deviceId}>
              <div>
                <strong>{device.name || device.deviceId}</strong>
                <div className="dashboard__muted">{device.location || "-"}</div>
              </div>
              <div>{device.status === "online" ? t("common.online") : t("common.offline")}</div>
              <div>
                {device.hasDeviceAccount
                  ? device.accountUsername || t("devices.account")
                  : t("devices.noAccount")}
              </div>
              <div>{formatDateTime(device.lastSeenAt)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
